import React, { useRef } from "react";
import { Play, Clock, Languages } from "lucide-react";
import styles from "./Css/TrendingNow.module.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Mousewheel, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { useTrendingAudios } from "../../hooks/useHomePageHooks";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const TrendingNow = () => {
  const navigationPrevRef = useRef(null);
  const navigationNextRef = useRef(null);
  const { trendingAudios, isLoading: isLoadingAudios } = useTrendingAudios();
  const navigate = useNavigate();

  // Format duration from seconds to mm:ss format
  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // If there's no data and it's not loading, return null
  if (!isLoadingAudios && (!trendingAudios || trendingAudios.length === 0)) {
    return null;
  }

  // Render skeleton loading state
  const renderSkeletons = () => {
    return Array(5)
      .fill(0)
      .map((_, index) => (
        <SwiperSlide key={`skeleton-${index}`} className={styles.swiperSlide}>
          <div className={styles.mediaCard}>
            <div className={styles.imageWrapper}>
              <Skeleton
                height="100%"
                width="100%"
                baseColor="#2a2a2a"
                highlightColor="#3a3a3a"
              />
            </div>
            <div className={styles.mediaInfo}>
              <h3 className={styles.mediaTitle}>
                <Skeleton
                  width="90%"
                  baseColor="#2a2a2a"
                  highlightColor="#3a3a3a"
                />
              </h3>
              <div className={styles.mediaDetails}>
                <Skeleton
                  width="100%"
                  baseColor="#2a2a2a"
                  highlightColor="#3a3a3a"
                />
              </div>
            </div>
          </div>
        </SwiperSlide>
      ));
  };

  return (
    <section className={styles.trendingSection}>
      <div className={styles.trendingHeader}>
        <h1 className={styles.title}>Trending Now</h1>
      </div>

      <div className={styles.swiperContainer}>
        <Swiper
          freeMode={{
            enabled: true,
            momentum: true,
            momentumRatio: 1, // smoother momentum
            momentumVelocityRatio: 1, // normal velocity handling
            minimumVelocity: 0.02, // keep this small to allow tiny moves
          }}
          modules={[Navigation, Mousewheel, FreeMode]}
          mousewheel={{
            forceToAxis: true,
            sensitivity: 0.5, // Lower sensitivity for smoother movement
            releaseOnEdges: true,
          }}
          speed={400} // Reduce speed slightly for softer acceleration
          resistance={true} // Allow a bit of resistance (helps feel natural)
          resistanceRatio={0.85}
          breakpoints={{
            320: {
              slidesPerView: 3,
              spaceBetween: 8,
            },
            480: {
              slidesPerView: 3,
              spaceBetween: 10,
            },
            768: {
              slidesPerView: 4,
              spaceBetween: 10,
            },
            1024: {
              slidesPerView: 7,
              spaceBetween: 12,
            },
          }}
          onBeforeInit={(swiper) => {
            swiper.params.navigation.prevEl = navigationPrevRef.current;
            swiper.params.navigation.nextEl = navigationNextRef.current;
          }}
          className={styles.swiperWrapper}
        >
          {isLoadingAudios
            ? renderSkeletons()
            : trendingAudios.map((item) => (
                <SwiperSlide key={item.id} className={styles.swiperSlide}>
                  <div className={styles.mediaCard}>
                    <div className={styles.imageWrapper}>
                      <img
                        src={item.mobile_image_url}
                        alt={item.title}
                        className={styles.mediaImage}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/audio-play/${item.slug}`)}
                      />
                      <div className={styles.overlay} />
                      <div className={styles.mediaLabel}>
                        <span
                          className={styles.labelText}
                          onClick={() =>
                            navigate(`/collection/category/${item.genre_name}`)
                          }
                        >
                          {item.genre_name}
                        </span>
                      </div>
                    </div>
                    <div className={styles.mediaInfo}>
                      <div className={styles.mediaDetails}>
                        <span className={styles.detailBadge}>
                          <Clock size={14} />
                          &nbsp;{formatDuration(item.duration)}
                        </span>
                        |
                        <span className={styles.detailBadge}>
                          <Languages size={14} />
                          &nbsp;{item.language}
                        </span>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
        </Swiper>
      </div>
    </section>
  );
};

export default TrendingNow;
